/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

// PrimeNG imports
import { ConfirmationService, MessageService } from "primeng/api";
import { AccordionModule } from "primeng/accordion";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { InputTextModule } from "primeng/inputtext";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SelectModule } from "primeng/select";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
import { MessageModule } from "primeng/message";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";

// Our constants
import {
  COUNTRIES,
  STATES,
} from "../../../../shared/constants/address.constants";

// Our services
import { CustomersService } from "../../services/customers.service";

// Our interfaces
import { Customer } from "../../interfaces/customer.interface";
import {
  IAddressForm,
  ICustomerEditForm,
} from "../../interfaces/customer-edit-form.interface";
import { PaymentMethod } from "../../interfaces/customer-payment-method.interface";

// Our components
import { CustomerPaymentMethodsSectionComponent } from "../../components/customer-payment-methods-section/customer-payment-methods-section.component";
import { AddPaymentMethodDialogComponent } from "../../components/add-payment-method-dialog/add-payment-method-dialog.component";
import { CustomerHistoryDialogComponent } from "../../components/customer-history-dialog/customer-history-dialog.component";
import { CustomerEditSkeletonComponent } from "./customer-edit-skeleton.component";

/**
 * Customer Edit Component
 * Editable form for customer information
 */
@Component({
  selector: "app-customer-edit",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AccordionModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmDialogModule,
    InputTextModule,
    MessageModule,
    ProgressSpinnerModule,
    SelectModule,
    ToastModule,
    TooltipModule,
    CustomerPaymentMethodsSectionComponent,
    CustomerEditSkeletonComponent,
  ],
  providers: [ConfirmationService, DialogService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  templateUrl: "./customer-edit.component.html",
  styleUrl: "./customer-edit.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerEditComponent implements OnInit, OnDestroy {
  // Dual mode inputs for web component/iframe support
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputCustomerId?: number;
  @Input() embeddedMode: boolean = false;

  // Injected services
  private readonly elementRef = inject(ElementRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private customersService = inject(CustomersService);
  private sessionService = inject(SessionService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);

  // State signals
  customerId = signal<number>(0);
  siteId = signal<number>(0);
  customer = signal<Customer | null>(null);
  loading = signal<boolean>(true);
  saving = signal<boolean>(false);
  error = signal<string | null>(null);

  // Payment methods state
  paymentMethods = signal<PaymentMethod[]>([]);
  paymentMethodsLoading = signal<boolean>(false);
  private dialogRef: DynamicDialogRef | null = null;
  private originalPaymentMethods: PaymentMethod[] = []; // Track original for comparison

  // Form
  formGroup!: FormGroup<ICustomerEditForm>;

  // Address sync conflict message
  addressSyncConflict = signal<string | null>(null);

  // Dropdown options with formatted labels
  countryOptions = COUNTRIES.map((c) => ({
    ...c,
    label: `${c.name} (${c.alpha2})`,
  }));
  stateOptions = STATES.map((s) => ({
    ...s,
    label: `${s.name} (${s.abbreviation})`,
  }));

  // Original data for comparison
  private originalData: Customer | null = null;

  // Computed values
  fullName = computed(() => {
    const c = this.customer();
    if (!c) return "Customer";
    return `${c.FirstName || ""} ${c.LastName || ""}`.trim() || "Customer";
  });

  pageTitle = computed(() => `Edit: ${this.fullName()}`);

  // Track if form has changes
  hasUnsavedChanges = signal<boolean>(false);

  // Accordion state - all open by default (indices: 0=Basic Info, 1=Addresses, 2=Payment)
  accordionActiveIndices = signal<number[]>([0, 1, 2]);

  // Cleanup
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initializeForm();

    // Detect embedded mode from attribute (for web components)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");
    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get token from attribute in embedded mode
    if (this.embeddedMode) {
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      // Create inputToken from attribute if:
      // 1. We have a token string from attribute
      // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
      // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }

      // Get siteId and customerId from attributes
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      const customerIdString =
        this.inputCustomerId?.toString() ??
        element.getAttribute("input-customer-id") ??
        element.getAttribute("inputCustomerId");

      if (siteIdString) {
        this.siteId.set(parseInt(siteIdString, 10));
      }
      if (customerIdString) {
        const id = parseInt(customerIdString, 10);
        this.customerId.set(id);
        this.loadCustomer(id);
        this.loadPaymentMethods(id);
      }
    } else {
      // Standard mode: get from route params
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const id = Number(params["id"]);
        const siteId = Number(params["siteId"]) || 0;
        this.customerId.set(id);
        this.siteId.set(siteId);
        this.loadCustomer(id);
        this.loadPaymentMethods(id);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dialogRef?.close();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Warn user about unsaved changes when leaving
   */
  @HostListener("window:beforeunload", ["$event"])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
      $event.returnValue = "";
    }
  }

  /**
   * Initialize the form with proper structure
   */
  private initializeForm(): void {
    this.formGroup = this.fb.nonNullable.group({
      email: ["", [Validators.required, Validators.email]],
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      company: [null as string | null],
      phone: [null as string | null],
      externalId: [null as string | null], // Platform Customer ID
      shipping: this.createAddressFormGroup(),
      billing: this.createAddressFormGroup(),
      billingMatchesShipping: [false], // Toggle for "Billing same as shipping"
      shippingMatchesBilling: [false], // Toggle for "Shipping same as billing"
    });

    // Subscribe to form changes to track dirty state
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkForChanges();
    });

    // Subscribe to billingMatchesShipping toggle
    this.formGroup.controls.billingMatchesShipping.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((matches) => {
        if (matches) {
          // Check if the other toggle is active
          if (this.formGroup.controls.shippingMatchesBilling.value) {
            this.addressSyncConflict.set(
              "Cannot enable - Shipping is set to match Billing. Uncheck that option first.",
            );
            // Revert the checkbox
            this.formGroup.controls.billingMatchesShipping.setValue(false, {
              emitEvent: false,
            });
            // Auto-clear message after 4 seconds
            setTimeout(() => this.addressSyncConflict.set(null), 4000);
            return;
          }
          this.addressSyncConflict.set(null);
          this.syncBillingFromShipping();
          this.formGroup.controls.billing.disable();
        } else {
          this.formGroup.controls.billing.enable();
        }
      });

    // Subscribe to shippingMatchesBilling toggle
    this.formGroup.controls.shippingMatchesBilling.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((matches) => {
        if (matches) {
          // Check if the other toggle is active
          if (this.formGroup.controls.billingMatchesShipping.value) {
            this.addressSyncConflict.set(
              "Cannot enable - Billing is set to match Shipping. Uncheck that option first.",
            );
            // Revert the checkbox
            this.formGroup.controls.shippingMatchesBilling.setValue(false, {
              emitEvent: false,
            });
            // Auto-clear message after 4 seconds
            setTimeout(() => this.addressSyncConflict.set(null), 4000);
            return;
          }
          this.addressSyncConflict.set(null);
          this.syncShippingFromBilling();
          this.formGroup.controls.shipping.disable();
        } else {
          this.formGroup.controls.shipping.enable();
        }
      });

    // Subscribe to shipping changes when billing matches shipping
    this.formGroup.controls.shipping.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.formGroup.controls.billingMatchesShipping.value) {
          this.syncBillingFromShipping();
        }
      });

    // Subscribe to billing changes when shipping matches billing
    this.formGroup.controls.billing.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.formGroup.controls.shippingMatchesBilling.value) {
          this.syncShippingFromBilling();
        }
      });
  }

  /**
   * Sync billing address from shipping address
   */
  private syncBillingFromShipping(): void {
    const shippingValue = this.formGroup.controls.shipping.getRawValue();
    this.formGroup.controls.billing.patchValue(shippingValue, {
      emitEvent: false,
    });
  }

  /**
   * Sync shipping address from billing address
   */
  private syncShippingFromBilling(): void {
    const billingValue = this.formGroup.controls.billing.getRawValue();
    this.formGroup.controls.shipping.patchValue(billingValue, {
      emitEvent: false,
    });
  }

  /**
   * Create an address form group
   */
  private createAddressFormGroup(): FormGroup<IAddressForm> {
    return this.fb.nonNullable.group({
      firstName: [""],
      lastName: [""],
      street1: [""],
      street2: [null as string | null],
      city: [""],
      state: [""],
      postcode: [""],
      country: [""],
    });
  }

  /**
   * Load customer data
   */
  private loadCustomer(id: number): void {
    this.loading.set(true);
    this.error.set(null);
    const accessToken = this.getAccessToken();

    this.customersService
      .getCustomer(id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customer) => {
          this.customer.set(customer);
          this.originalData = { ...customer };
          // Use customer's SiteId if route param wasn't provided
          if (!this.siteId() && customer.SiteId) {
            this.siteId.set(customer.SiteId);
          }
          this.populateForm(customer);
          this.loading.set(false);
        },
        error: (err) => {
          console.error("Failed to load customer:", err);
          this.error.set("Failed to load customer. Please try again.");
          this.loading.set(false);
        },
      });
  }

  /**
   * Populate form with customer data
   * Uses PascalCase field names matching API response
   */
  private populateForm(customer: Customer): void {
    // First, enable billing to allow patching values
    this.formGroup.controls.billing.enable({ emitEvent: false });

    this.formGroup.patchValue(
      {
        email: customer.Email || "",
        firstName: customer.FirstName || "",
        lastName: customer.LastName || "",
        company: customer.ShippingCompany || null, // Use ShippingCompany for display
        phone: customer.PhoneNumber || null, // API uses PhoneNumber
        externalId: customer.ExternalId || null, // Platform Customer ID
        shipping: {
          firstName: customer.ShippingFirstName || "",
          lastName: customer.ShippingLastName || "",
          street1: customer.ShippingStreet1 || "",
          street2: customer.ShippingStreet2 || null,
          city: customer.ShippingCity || "",
          state: customer.ShippingState || "",
          postcode: customer.ShippingPostcode || "",
          country: customer.ShippingCountry || "",
        },
        billing: {
          firstName: customer.BillingFirstName || "",
          lastName: customer.BillingLastName || "",
          street1: customer.BillingStreet1 || "",
          street2: customer.BillingStreet2 || null,
          city: customer.BillingCity || "",
          state: customer.BillingState || "",
          postcode: customer.BillingPostcode || "",
          country: customer.BillingCountry || "",
        },
      },
      { emitEvent: false },
    );

    // Check if addresses match and set the toggle (default to billing matches shipping)
    const addressesMatch = this.checkAddressesMatch();
    if (addressesMatch) {
      // Default: set billing matches shipping when addresses are the same
      this.formGroup.controls.billingMatchesShipping.setValue(true, {
        emitEvent: false,
      });
      this.formGroup.controls.shippingMatchesBilling.setValue(false, {
        emitEvent: false,
      });
      this.formGroup.controls.billing.disable({ emitEvent: false });
    } else {
      this.formGroup.controls.billingMatchesShipping.setValue(false, {
        emitEvent: false,
      });
      this.formGroup.controls.shippingMatchesBilling.setValue(false, {
        emitEvent: false,
      });
    }

    // Reset change tracking after initial population
    this.hasUnsavedChanges.set(false);
  }

  /**
   * Check if shipping and billing addresses match
   */
  private checkAddressesMatch(): boolean {
    const shipping = this.formGroup.controls.shipping.getRawValue();
    const billing = this.formGroup.controls.billing.getRawValue();

    return (
      shipping.firstName === billing.firstName &&
      shipping.lastName === billing.lastName &&
      shipping.street1 === billing.street1 &&
      (shipping.street2 || "") === (billing.street2 || "") &&
      shipping.city === billing.city &&
      shipping.state === billing.state &&
      shipping.postcode === billing.postcode &&
      shipping.country === billing.country
    );
  }

  /**
   * Check if form has changes compared to original
   * Uses PascalCase field names matching API response
   */
  private checkForChanges(): void {
    if (!this.originalData) {
      this.hasUnsavedChanges.set(false);
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const original = this.originalData;

    const hasFormChanges =
      formValue.email !== (original.Email || "") ||
      formValue.firstName !== (original.FirstName || "") ||
      formValue.lastName !== (original.LastName || "") ||
      formValue.company !== (original.ShippingCompany || null) ||
      formValue.phone !== (original.PhoneNumber || null) ||
      formValue.externalId !== (original.ExternalId || null) ||
      formValue.shipping.firstName !== (original.ShippingFirstName || "") ||
      formValue.shipping.lastName !== (original.ShippingLastName || "") ||
      formValue.shipping.street1 !== (original.ShippingStreet1 || "") ||
      formValue.shipping.street2 !== (original.ShippingStreet2 || null) ||
      formValue.shipping.city !== (original.ShippingCity || "") ||
      formValue.shipping.state !== (original.ShippingState || "") ||
      formValue.shipping.postcode !== (original.ShippingPostcode || "") ||
      formValue.shipping.country !== (original.ShippingCountry || "") ||
      formValue.billing.firstName !== (original.BillingFirstName || "") ||
      formValue.billing.lastName !== (original.BillingLastName || "") ||
      formValue.billing.street1 !== (original.BillingStreet1 || "") ||
      formValue.billing.street2 !== (original.BillingStreet2 || null) ||
      formValue.billing.city !== (original.BillingCity || "") ||
      formValue.billing.state !== (original.BillingState || "") ||
      formValue.billing.postcode !== (original.BillingPostcode || "") ||
      formValue.billing.country !== (original.BillingCountry || "");

    // Check for payment method changes
    const hasPaymentMethodChanges = this.hasPaymentMethodChanges();

    this.hasUnsavedChanges.set(hasFormChanges || hasPaymentMethodChanges);
  }

  /**
   * Check if payment methods have changed from original
   */
  private hasPaymentMethodChanges(): boolean {
    const current = this.paymentMethods();
    const original = this.originalPaymentMethods;

    // Different count means changes
    if (current.length !== original.length) {
      return true;
    }

    // Check if all IDs match (order doesn't matter)
    const currentIds = current.map((m) => m.Id).sort();
    const originalIds = original.map((m) => m.Id).sort();

    return !currentIds.every((id, index) => id === originalIds[index]);
  }

  /**
   * Helper to convert empty strings to null (matching old QMC behavior)
   */
  private emptyToNull(value: string | null | undefined): string | null {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return value;
  }

  /**
   * Save customer
   * Sends PascalCase field names matching API expectations
   * Matches old QMC payload format exactly
   */
  save(): void {
    if (this.formGroup.invalid) {
      // Mark all fields as touched to show validation errors
      this.formGroup.markAllAsTouched();
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please fix the errors before saving.",
      });
      return;
    }

    this.saving.set(true);
    const formValue = this.formGroup.getRawValue();
    const original = this.originalData;

    // Build update payload matching old QMC format exactly
    // Old QMC sends the full customer object with all fields
    const updateData: Partial<Customer> = {
      Id: this.customerId(),
      SiteId: this.siteId(),
      ExternalId: this.emptyToNull(formValue.externalId), // Platform Customer ID from form
      Email: formValue.email,
      FirstName: formValue.firstName,
      LastName: formValue.lastName,
      Created: original?.Created ?? null,
      // Shipping Address - use null for empty strings
      ShippingFirstName: this.emptyToNull(formValue.shipping.firstName),
      ShippingLastName: this.emptyToNull(formValue.shipping.lastName),
      ShippingStreet1: this.emptyToNull(formValue.shipping.street1),
      ShippingStreet2: this.emptyToNull(formValue.shipping.street2),
      ShippingCity: this.emptyToNull(formValue.shipping.city),
      ShippingState: this.emptyToNull(formValue.shipping.state),
      ShippingPostcode: this.emptyToNull(formValue.shipping.postcode),
      ShippingCountry: this.emptyToNull(formValue.shipping.country),
      // Billing Address - use null for empty strings
      BillingFirstName: this.emptyToNull(formValue.billing.firstName),
      BillingLastName: this.emptyToNull(formValue.billing.lastName),
      BillingStreet1: this.emptyToNull(formValue.billing.street1),
      BillingStreet2: this.emptyToNull(formValue.billing.street2),
      BillingCity: this.emptyToNull(formValue.billing.city),
      BillingState: this.emptyToNull(formValue.billing.state),
      BillingPostcode: this.emptyToNull(formValue.billing.postcode),
      BillingCountry: this.emptyToNull(formValue.billing.country),
      // PhoneNumber and Company fields (API uses these exact names)
      PhoneNumber: this.emptyToNull(formValue.phone),
      ShippingCompany: this.emptyToNull(formValue.company),
      BillingCompany: this.emptyToNull(formValue.company), // Same as shipping for now
      // Metadata fields from original (preserve them)
      LifetimeValue: original?.LifetimeValue ?? 0,
      CreatedUtc: original?.CreatedUtc ?? null,
      UpdatedUtc: original?.UpdatedUtc ?? null,
      IsShowingNotes: original?.IsShowingNotes ?? true,
      IsShowCoupons: original?.IsShowCoupons ?? true,
      IsProcessingPaused: original?.IsProcessingPaused ?? false,
      // Include current payment methods (may have been modified locally)
      PaymentMethods: this.paymentMethods(),
    };

    const accessToken = this.getAccessToken();
    this.customersService
      .updateCustomer(this.customerId(), updateData, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedCustomer) => {
          this.saving.set(false);
          this.hasUnsavedChanges.set(false);
          this.originalData = { ...updatedCustomer };
          this.customer.set(updatedCustomer);
          // Update original payment methods after save
          this.originalPaymentMethods = [...this.paymentMethods()];

          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Customer saved successfully.",
          });

          // Stay on edit page - don't navigate away
        },
        error: (err) => {
          console.error("Failed to save customer:", err);
          this.saving.set(false);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to save customer. Please try again.",
          });
        },
      });
  }

  /**
   * Cancel editing with confirmation if there are changes
   */
  cancel(): void {
    if (this.hasUnsavedChanges()) {
      this.confirmationService.confirm({
        message: "You have unsaved changes. Are you sure you want to leave?",
        header: "Unsaved Changes",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Leave",
        rejectLabel: "Stay",
        acceptButtonStyleClass: "p-button-danger",
        accept: () => {
          this.hasUnsavedChanges.set(false);
          this.navigateToList();
        },
      });
    } else {
      this.navigateToList();
    }
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    if (this.hasUnsavedChanges()) {
      this.confirmationService.confirm({
        message: "You have unsaved changes. Are you sure you want to leave?",
        header: "Unsaved Changes",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Leave",
        rejectLabel: "Stay",
        acceptButtonStyleClass: "p-button-danger",
        accept: () => {
          this.hasUnsavedChanges.set(false);
          this.navigateToList();
        },
      });
    } else {
      this.navigateToList();
    }
  }

  /**
   * Navigate to list
   * Emits event in embedded mode for parent to handle navigation
   */
  private navigateToList(): void {
    const siteId = this.siteId();

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "customer-edit",
          action: "list",
          customerId: this.customerId(),
          siteId: siteId,
        },
        bubbles: true,
        composed: true, // Crosses shadow DOM boundary
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Use standard routing
      if (siteId) {
        void this.router.navigate(["/widgets/dashboard", siteId, "customers"]);
      } else {
        void this.router.navigate(["/customers"]);
      }
    }
  }

  /**
   * Retry loading
   */
  retry(): void {
    const id = this.customerId();
    if (id) {
      this.loadCustomer(id);
    }
  }

  /**
   * Check if a field has an error
   */
  hasError(controlPath: string): boolean {
    const control = this.formGroup.get(controlPath);
    return !!control && control.invalid && control.touched;
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(controlPath: string): string {
    const control = this.formGroup.get(controlPath);
    if (!control || !control.errors) return "";

    if (control.errors["required"]) return "This field is required";
    if (control.errors["email"]) return "Please enter a valid email";

    return "Invalid value";
  }

  /**
   * Check if shipping country is USA
   * Used to determine whether to show state dropdown or text input
   */
  isShippingUSA(): boolean {
    const country = this.formGroup.controls.shipping.controls.country.value;
    return country === "US" || country === "USA";
  }

  /**
   * Check if billing country is USA
   * Used to determine whether to show state dropdown or text input
   */
  isBillingUSA(): boolean {
    const country = this.formGroup.controls.billing.controls.country.value;
    return country === "US" || country === "USA";
  }

  /**
   * Handle shipping country change - clear state when switching countries
   */
  onShippingCountryChange(): void {
    this.formGroup.controls.shipping.controls.state.setValue("");
  }

  /**
   * Handle billing country change - clear state when switching countries
   */
  onBillingCountryChange(): void {
    this.formGroup.controls.billing.controls.state.setValue("");
  }

  // ============================================
  // Payment Methods Section
  // ============================================

  /**
   * Load payment methods for the customer
   */
  private loadPaymentMethods(customerId: number): void {
    this.paymentMethodsLoading.set(true);
    const accessToken = this.getAccessToken();

    this.customersService
      .getPaymentMethods(customerId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (methods) => {
          this.paymentMethods.set(methods);
          this.originalPaymentMethods = [...methods]; // Store original for comparison
          this.paymentMethodsLoading.set(false);
        },
        error: (err) => {
          console.error("Failed to load payment methods:", err);
          this.paymentMethodsLoading.set(false);
        },
      });
  }

  /**
   * Open the add payment method dialog
   */
  openAddPaymentMethodDialog(): void {
    this.dialogRef = this.dialogService.open(AddPaymentMethodDialogComponent, {
      header: "Add Payment Method",
      width: "min(800px, 95vw)",
      modal: true,
      closable: true,
      contentStyle: {
        padding: "0 1.5rem 1rem 1.5rem",
        overflow: "auto",
        maxHeight: "calc(80vh - 60px)",
      },
      styleClass: "add-payment-method-modal",
      breakpoints: {
        "768px": {
          width: "95vw",
          contentStyle: {
            padding: "0 0.75rem 0.75rem 0.75rem",
            overflow: "auto",
            maxHeight: "calc(85vh - 50px)",
          },
        },
      },
      data: {
        customerId: this.customerId(),
        siteId: this.siteId(),
        existingMethods: this.paymentMethods(),
        embeddedMode: this.embeddedMode,
        inputToken: this.inputToken,
      },
    });

    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          // Refresh payment methods after adding (don't update original - that happens on save)
          const accessToken = this.getAccessToken();
          this.customersService
            .getPaymentMethods(this.customerId(), accessToken)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (methods) => {
                this.paymentMethods.set(methods);
                // Check for changes (will detect new payment method)
                this.checkForChanges();
                this.messageService.add({
                  severity: "info",
                  summary: "Payment Method Added",
                  detail: "Save the customer to persist this change.",
                });
              },
            });
        }
      });
  }

  /**
   * Delete a payment method (local only - saved with customer update)
   */
  onDeletePaymentMethod(paymentMethodId: number): void {
    this.confirmationService.confirm({
      message:
        "This payment method will be removed when you save the customer. Continue?",
      header: "Remove Payment Method",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        // Remove from local array (don't call API)
        const currentMethods = this.paymentMethods();
        const updatedMethods = currentMethods.filter(
          (m) => m.Id !== paymentMethodId,
        );
        this.paymentMethods.set(updatedMethods);

        // Check for changes (will detect removed payment method)
        this.checkForChanges();

        this.messageService.add({
          severity: "info",
          summary: "Payment Method Removed",
          detail: "Save the customer to persist this change.",
        });
      },
    });
  }

  // ============================================
  // Customer History Section
  // ============================================

  /**
   * Open the customer history dialog
   * Passes embedded mode context if available
   */
  openCustomerHistoryDialog(): void {
    this.dialogService.open(CustomerHistoryDialogComponent, {
      header: "History",
      width: "min(600px, 95vw)",
      modal: true,
      closable: true,
      contentStyle: { overflow: "visible", padding: "0.75rem" },
      styleClass: "history-dialog-modal",
      data: {
        customerId: this.customerId(),
        siteId: this.siteId(),
        embeddedMode: this.embeddedMode,
        inputToken: this.inputToken,
      },
    });
  }
}
